import React, {useContext, useEffect} from "react"
import {useTranslation} from "next-i18next"
import {CSSTransition, TransitionGroup} from "react-transition-group"
import CartContext from "../../context/CartContext"
import CheckoutContext from "../../context/CheckoutContext"
import {Trash} from "react-bootstrap-icons"
import {StockStatus} from "../bikeDetails/BikeDetails"
import Typography from "../reusable/Typography/Typography"
import OrderPriceWarning from "./OrderPriceWarning"
import AnimationComponent from "../reusable/components/AnimationComponent/AnimationComponent"
import {ecommerceEvents, gaCategories, gaEvents} from "../../config/googleAnalytics/events"
import AnalyticsContext from "../../context/AnalyticsContext"
import {customPlanLengthMap} from "../bikeDetails/plan/customPlanLengthMap"
import DiscountCodeContext from "../../context/DiscountCodeContext"
import OrganizationContext from "../../context/OrganizationContext"
import {formatPrice} from "../../utility/Helper"

const Cart: React.FC<{ showPriceWarning: boolean }> = ({showPriceWarning}) => {
	const {t} = useTranslation()
	const {items, removeItem} = useContext(CartContext)
	const {data} = useContext(CheckoutContext)
	const {organizationSlug} = useContext(OrganizationContext)
	const {discountCode} = useContext(DiscountCodeContext)
	const {reactGA} = useContext(AnalyticsContext)
	useEffect(() => {
		const itemsToRemove = []
		for (const item of items) {
			if (!item.product.finalPriceWithoutDownPayment) {
				itemsToRemove.push(item)
			}
		}

		for (const itemToRemove of itemsToRemove) {
			removeItem(itemToRemove)
		}
	}, [items])

	return (
		<>
			<div className={"row d-block d-md-none g-0 mb-5"}>
				<div className={"col-12"}>
					<Typography variant="heading2"
						semanticTag="h1"
						className={"text-center text-md-start mb-3"}>
						{t("Warenkorb")}
					</Typography>
				</div>
				<AnimationComponent type="slideRight"
					inProp={showPriceWarning}>
					<OrderPriceWarning/>
				</AnimationComponent>
				<TransitionGroup className={"col-12"}>
					{items.map(item => {
						const finalPriceWithoutDownPayment = parseFloat(item.product.finalPriceWithoutDownPayment || item.product.price)
						const planLengthMap = customPlanLengthMap[process.env.NEXT_PUBLIC_DISCOUNTS_INSTANCE]
						const planLength = data?.planLength ? planLengthMap ? planLengthMap[data?.planLength] : data?.planLength : 48
						const pricePerMonth = planLength > 1 ? (finalPriceWithoutDownPayment - (item.downPayment || 0)) / (planLength) : finalPriceWithoutDownPayment / (planLength)
						const brand = item.product.attributes?.find((e:any) => e.slug === "brand")?.terms[0].name
						const frame = item.product.attributes?.find((e:any) => e.slug === "frame_variant")?.terms[0].name ?? ""
						const soldOut = item.product?.stock_status === StockStatus.outOfStock
						const {onSale} = item.product
						const _onSale = onSale

						return (
							<CSSTransition
								key={item.uniqueId}
								timeout={500}
								classNames="delete-animation"
							>
								<div key={item.uniqueId}
									className={"row align-items-center mb-4"}
									style={soldOut ? {
										opacity: 0.5
									} : null}>
									<div className={"col-9"}>
										<div className={"row align-items-center"}>
											<div className={"col-3"}>
												<img alt={""}
													src={item.product.images[0]?.src}
													width={"100%"}
													height={"auto"}/>
											</div>
											<div className={"col-9"}>
												{soldOut ? <Typography variant="bodyLg"
													semanticTag="span"
													className="badge bg-secondary text-uppercase ps-3 pe-3 mb-2">
													{t("Ausverkauft")}
												</Typography> : null}
												<div>
													<Typography variant="bodyLg"
														semanticTag="span"
														className={"text-secondary text-uppercase"}>
														{brand}
													</Typography>
												</div>
												<div>
													<Typography variant="bodyLgBold"
														semanticTag="span">
														{item.product.name}
													</Typography>
												</div>
												<div>
													<Typography variant="bodyLg"
														semanticTag="span"
														className={"text-secondary"}>
														{frame && `${item.selectedSize}, ${frame}`}
													</Typography>
												</div>
											</div>
										</div>
									</div>
									<div className={"col-2 text-end"}>
										<div className="row">
											<div className="col-12">
												<Typography variant="bodyLgBold"
													semanticTag="span"
													className={`text-bold ${_onSale ? "text-danger" : ""}`}>
													{`CHF ${Math.ceil(pricePerMonth)}`}
												</Typography>
												<Typography variant={"bodyXSmBold"}
													semanticTag={"span"}
													className={_onSale ? "text-danger" : "text-secondary"}>
													{" / Monat"}
												</Typography>
											</div>
											<div className="col-12">
												<button type={"button"}
													className={"btn"}
													onClick={() => {
														removeItem(item)
														reactGA?.event({
															category: gaCategories.cart,
															action: gaEvents.deleteFromCart,
															label: gaEvents.deleteFromCart,
															nonInteraction: false
														})

														reactGA?.gtag(ecommerceEvents.event, gaEvents.deleteFromCart, {
															currency: ecommerceEvents.CHF,
															value: finalPriceWithoutDownPayment,
															items: [
																{
																	item_id: item?.product?.id,
																	item_name: item?.product?.name
																}
															]})
													}}>
													<Trash size={24}
														className={"text-secondary"}/>
												</button>
											</div>
										</div>
									</div>
								</div>
							</CSSTransition>
						)
					})}
					{items.length === 0 ? <Typography variant="bodyLg"
						semanticTag="span"
						className={"text-secondary"}>{t("Dein Warenkorb ist leer.")}</Typography> : null}
					{(discountCode.isMandatoryForCheckout && (!discountCode.code || discountCode.notApplicable || discountCode.discountAmount === 0)) ? <Typography className="text-start text-danger mt-2 mb-2"
						variant="bodySm">{t("discount-code-is-required").replace("organization", organizationSlug)}</Typography> : null}
				</TransitionGroup>
			</div>
			<div className={"row d-none d-md-block"}>
				<div className={"col-12"}>
					<Typography variant="heading2"
						semanticTag="h1">
						{t("Warenkorb")}
					</Typography>
				</div>
				<AnimationComponent type="slideRight"
					inProp={showPriceWarning}>
					<OrderPriceWarning/>
				</AnimationComponent>
				<TransitionGroup className="col-12 mb-5">
					{items.map(item => {
						const finalPriceWithoutDownPayment = parseFloat(item.product.finalPriceWithoutDownPayment || item.product.price)
						const planLengthMap = customPlanLengthMap[process.env.NEXT_PUBLIC_DISCOUNTS_INSTANCE]
						const planLength = data?.planLength ? planLengthMap ? planLengthMap[data?.planLength] : data?.planLength : 48
						const pricePerMonth = planLength > 1 ? (finalPriceWithoutDownPayment - (item.downPayment || 0)) / (planLength) : finalPriceWithoutDownPayment / (planLength)
						const brand = item.product.attributes?.find((e:any) => e.slug === "brand")?.terms[0].name
						const frame = item.product.attributes?.find((e:any) => e.slug === "frame_variant")?.terms[0].name
						const soldOut = item.product?.stock_status === StockStatus.outOfStock
						const {onSale} = item.product
						const _onSale = onSale

						return (
							<CSSTransition
								key={item.uniqueId}
								timeout={500}
								classNames="delete-animation"
							>
								<div key={item.uniqueId}
									className={"row align-items-center"}
									style={soldOut ? {
										opacity: 0.5
									} : null}>
									<div cypress-monthly-price-class={`monthly-price-value-${pricePerMonth.toFixed(2)}`}
										className={"d-none}"} />
									<div className={"col-auto"}>
										<button type={"button"}
											className={"btn"}
											onClick={() => {
												removeItem(item)
												reactGA?.event({
													category: gaCategories.cart,
													action: gaEvents.deleteFromCart,
													label: gaEvents.deleteFromCart,
													nonInteraction: false
												})

												reactGA?.gtag(ecommerceEvents.event, gaEvents.deleteFromCart, {
													currency: ecommerceEvents.CHF,
													value: finalPriceWithoutDownPayment,
													items: [
														{
															item_id: item?.product?.id,
															item_name: item?.product?.name
														}
													]})
											}}>
											<Trash size={24}
												className={"text-secondary"}/>
										</button>
									</div>
									<div className={"col"}>
										<div className={"row align-items-center"}>
											<div className={"col-3"}>
												<img alt={""}
													src={item.product.images[0]?.src}
													width={"100%"}
													height={"auto"}/>
											</div>
											<div className={"col-9"}>
												{soldOut ?
													<span className="badge bg-secondary text-uppercase ps-3 pe-3 mb-2">
														{t("Ausverkauft")}
													</span> : null}
												<div>
													<Typography variant="bodyLg"
														semanticTag="span"
														className={"text-secondary text-uppercase"}>
														{brand}
													</Typography>
												</div>
												<div>
													<Typography variant="bodyLgBold"
														semanticTag="span">
														{item.product.name}
													</Typography>
												</div>
												<div>
													<Typography variant="bodyLg"
														semanticTag="span"
														className={"text-secondary"}>
														{frame && `${item.selectedSize}, ${frame}`}
													</Typography>
												</div>
											</div>
										</div>
									</div>
									<div className={"col-3 text-start"}>
										<Typography variant={"bodyXSmBold"}
											semanticTag={"span"}
											className={_onSale ? "text-danger" : "text-secondary"}>
											{"CHF "}
										</Typography>
										<Typography variant="heading3"
											semanticTag="span"
											className={`text-bold ${_onSale ? "text-danger" : ""}`}>
											{`${Math.ceil(pricePerMonth)}`}
										</Typography>
										<Typography variant={"bodyXSmBold"}
											semanticTag={"span"}
											className={_onSale ? "text-danger" : "text-secondary"}>
											{" / Monat"}
										</Typography>
										<div>
											{_onSale ? <>
												<div>
													<Typography variant={"bodyXSmBold"}
														semanticTag={"span"}
														className={"text-danger"}>
														{`Total: ${formatPrice(finalPriceWithoutDownPayment)}`}
													</Typography>
												</div>
												<div>
													<Typography variant={"bodyXSm"}
														semanticTag={"span"}
														className={"text-secondary text-decoration-line-through"}>
														{`${item.product.uvpPrice.toFixed(2)}`}
													</Typography>
													<Typography variant={"bodyXSm"}
														semanticTag={"span"}
														className={"text-secondary"}>
														{" (UVP)"}
													</Typography>
												</div>
											</> :
												<div>
													<Typography variant={"bodyXSmBold"}
														semanticTag={"span"}
														className={"text-secondary"}>
														{`Total: CHF ${formatPrice(finalPriceWithoutDownPayment)}`}
													</Typography>
												</div>}
										</div>
									</div>
								</div>
							</CSSTransition>
						)
					})}
				</TransitionGroup>
				{items.length < 1 ? <Typography variant="bodyLg"
					semanticTag="span"
					className={"text-secondary"}>{t("Dein Warenkorb ist leer.")}</Typography> : null}
			</div>

		</>
	)
}

export default Cart
